import { ReactComponent as StarGrey } from "assets/images/starGrey.svg"
import { useToggle } from "hooks"
import {
  Icon,
  IconButton,
  IconCertified,
  useWindowWidth,
  type IconType,
} from "materia"
import type { PropsWithChildren, ReactNode } from "react"
import { useTranslation } from "react-i18next"
import { modifiers, sanitizeHtml } from "ui-tools"
import { FiestaLink, type FiestaLinkProps } from "utility-components"
import { ProductModal } from ".."
import { usePageTitle } from "../../../tools/usePageTitle"
import { Badge } from "../../Badge"
import { Stars } from "../../Stars"
import { type Data, type Product } from "./gql"
import { DeadlineStatus, DiplomaStatus } from "./Status"

import s from "./ProductModalMain.module.scss"

export const ProductModalMain = ({
  productModalButtons,
  initialIsReadMoreVisible,
  data,
}: {
  productModalButtons: ReactNode
  initialIsReadMoreVisible?: boolean
  data: Data
}) => {
  usePageTitle(data.title)

  const [isReadMoreVisible, toggleReadMore] = useToggle(
    initialIsReadMoreVisible ?? false
  )

  const { isLocked } = data

  return (
    <ProductModal.Main>
      <div
        className={modifiers(s, "productModal", { isLocked })}
        aria-live="polite"
      >
        <Primary
          product={data}
          isReadMoreVisible={isReadMoreVisible}
          toggleReadMore={toggleReadMore}
          productModalButtons={productModalButtons}
        />
        <ReadMore
          product={data}
          isReadMoreVisible={isReadMoreVisible}
          toggleReadMore={toggleReadMore}
        />
      </div>
    </ProductModal.Main>
  )
}

const Primary = ({
  product,
  isReadMoreVisible,
  toggleReadMore,
  productModalButtons,
}: {
  product: Product
  isReadMoreVisible: boolean
  toggleReadMore: () => void
  productModalButtons: ReactNode
}) => {
  const { progress, isLocked } = product

  const isOneStarProduct = progress.starsMax === 1
  const isFiveStarProduct = progress.starsMax === 5

  const hasDiploma = Boolean(product?.diplomaConfiguration)

  const deadlineStatus = product?.deadlineOccurrence?.status
  // We need this atm as "white spacing" is not build into the product modal layout. Recommended in the future.
  const shouldShowDeadlineStatus =
    deadlineStatus && deadlineStatus !== "upcoming"

  return (
    <div className={modifiers(s, "primary", { isVisible: !isReadMoreVisible })}>
      <div className={s.badges}>
        {progress.certified && (
          <IconCertified title="Product status: Completed" size="large" />
        )}
        {isOneStarProduct && <ProductType product={product} />}
        {progress.mandatory && <Mandatory />}
      </div>
      <Image product={product} />
      <div className={s.bottom}>
        <div className={s.stars}>
          {isLocked ? (
            <StarGrey className={s.starGrey} />
          ) : (
            <Stars
              count={progress.starsCount}
              max={progress.starsMax}
              size="large"
            />
          )}
        </div>
        <Title product={product} />
        {isFiveStarProduct && <Score product={product} />}
        <Description product={product} toggleReadMore={toggleReadMore} />

        {hasDiploma && <DiplomaStatus product={product} />}
        {!hasDiploma && shouldShowDeadlineStatus && (
          <DeadlineStatus product={product} />
        )}

        {isLocked ? (
          <Locked product={product} />
        ) : (
          <Buttons>{productModalButtons}</Buttons>
        )}
      </div>
    </div>
  )
}

const ProductType = ({ product }: { product: Product }) => {
  const { t } = useTranslation()
  const { productType } = product
  const productTypeText = t(`product.PRODUCT_TYPES.${productType}`)
  return <Badge title={productTypeText} />
}

const Mandatory = () => {
  const { t } = useTranslation()
  return <Badge title={t("user.MANDATORY")} />
}

const Image = ({ product }: { product: Product }) => {
  const { image } = product
  return <img className={s.image} src={image} alt="" />
}

const Title = ({ product }: { product: Product }) => {
  const { title } = product
  return <h2 className={s.title}>{title}</h2>
}

const Score = ({ product }: { product: Product }) => {
  const { t } = useTranslation()

  const { progress } = product
  const { isBigWindow } = useWindowWidth()

  if (isBigWindow) return null

  if (progress.highScore === 0) return

  return (
    <div className={s.score}>{`${progress.highScore} ${t(
      "product.SCORE"
    )}`}</div>
  )
}

const Description = ({
  product,
  toggleReadMore,
}: {
  product: Product
  toggleReadMore: () => void
}) => {
  const { t } = useTranslation()

  const { descriptionLong, productType } = product
  const description = descriptionLong ?? ""

  const shortDescription = trimDescription(description, productType)
  const isButtonVisible = shortDescription.length < description.length

  return (
    <p className={s.description}>
      <span
        dangerouslySetInnerHTML={sanitizeHtml(shortDescription, "allow-links")}
      />
      {isButtonVisible && (
        <button
          className={s.description__showMore}
          onClick={toggleReadMore}
          aria-label="Read more about this product"
        >
          <span aria-hidden="true">...{t("product.PRODUCT_READ_MORE")}</span>
        </button>
      )}
    </p>
  )
}

const Locked = ({ product }: { product: Product }) => {
  const { t } = useTranslation()

  const lockedJourney = product.lockedBy.nodes[0]

  if (!lockedJourney) {
    throw new Error("Locked product must have a locked journey")
  }

  return (
    <div className={s.locked}>
      <button className={s.locked__button} disabled>
        <Icon icon="Lock" size="large" />
        <div className={s.locked__lockedTitle}>{t("product.LOCKED")}</div>
      </button>
      <div className={s.locked__description}>
        {t("product.LOCKED_COMPLETE_1")}
        <div className={s.locked__title}>{lockedJourney.title}</div>
        {t("product.LOCKED_COMPLETE_2")}
      </div>
    </div>
  )
}

const Buttons = ({ children }: PropsWithChildren) => (
  <ProductModal.ActionButtons>
    <div className={s.buttons}>{children}</div>
  </ProductModal.ActionButtons>
)

const PlayLink = ({
  iconVariant = "Play",
  linkVariant = "theme",
  children,
  ...props
}: FiestaLinkProps & {
  children: ReactNode
  iconVariant?: IconType
  linkVariant?: "theme" | "ghost"
}) => {
  // [TODO] https://jira.attensi.com/browse/WEB-19778
  // If the route we navigate to is "/play", then we want to add
  // it to the history stack, so we can navigate back with a browser
  // back button. [WEB-19159]
  const replace = props.to !== "play"

  return (
    <FiestaLink
      className={modifiers(s, "playLink", linkVariant)}
      replace={replace}
      {...props}
    >
      <Icon icon={iconVariant} />
      {children}
    </FiestaLink>
  )
}

const LanguageDropdown = ({ children }: PropsWithChildren) => (
  <div className={s.languageDropdown}>{children}</div>
)

const ReadMore = ({
  product,
  toggleReadMore,
  isReadMoreVisible,
}: {
  product: Product
  toggleReadMore: () => void
  isReadMoreVisible: boolean
}) => {
  const { title, descriptionLong } = product
  const description = descriptionLong ?? ""

  return (
    <div className={modifiers(s, "readMore", { isVisible: isReadMoreVisible })}>
      <header className={s.readMore__header}>
        <IconButton
          as="button"
          className={s.readMore__backButton}
          icon="Chevron"
          onClick={toggleReadMore}
          aria-label="Go back to product"
        />

        <h2 className={s.readMore__headerTitle}>{title}</h2>
      </header>
      <div className={s.readMore__description}>
        <span
          dangerouslySetInnerHTML={sanitizeHtml(description, "allow-links")}
        />
      </div>
    </div>
  )
}

ProductModalMain.PlayLink = PlayLink
ProductModalMain.LanguageDropdown = LanguageDropdown

const trimDescription = (
  description: string,
  productType: Data["productType"]
) => {
  // Those products do not have play buttons in the main product
  // modal body, so they have a bunch of free space which can be
  // taken by the description.
  const isProductWithoutPlayButtons = ["activity", "event"].includes(
    productType
  )
  const MAX_LENGTH = isProductWithoutPlayButtons ? 320 : 175
  const SPACE = " "
  const words = description.split(SPACE)

  let trimmedText = words[0] ?? ""
  for (const word of words.slice(1)) {
    const isTooLong = trimmedText.length + word.length > MAX_LENGTH
    if (isTooLong) break
    trimmedText += SPACE + word
  }

  return trimmedText
}
